import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { GuardianInvite } from '@se-po/shared-data-access-models'
import { Observable, zip } from 'rxjs'
import { SeAnalyticsService } from 'se-analytics'
import { SeFeModalComponent } from 'se-fe-modal'
import { SeFeToastService } from 'se-fe-toast'
import { SeFeTranslationsLoader } from 'se-fe-translations'
import { CentralServiceResources as cs } from 'se-resource-types'
import { Persona } from 'se-resource-types/dist/lib/CentralService/Profiles'
import { InviteComponent } from '../invite/invite.component'
import { AddGuardianModalSubmitter } from './add-guardian-modal-submit.service'

export enum AddGuardianModalStep {
  Invite,
  InviteReview
}

@Component({
  selector: 'se-po-add-guardian-modal',
  templateUrl: './add-guardian-modal.component.html',
  styleUrls: ['./add-guardian-modal.component.scss'],
})
export class AddGuardianModalComponent implements OnInit {
  @Output() public modalClosed: EventEmitter<Persona> = new EventEmitter()
  @ViewChild('modal') public modal: SeFeModalComponent
  @ViewChild('invite') public inviteStep: InviteComponent

  public error: string
  public guardianEmails: string[] = []
  public invites: GuardianInvite[] = []
  public loaded = false
  public persona: Persona
  public stepEnum: typeof AddGuardianModalStep = AddGuardianModalStep
  public step: AddGuardianModalStep = AddGuardianModalStep.Invite
  public translationKey = 'SE_PO_SE_PERSONA_PICKER_FEATURE_ADD_GUARDIAN_MODAL.'
  private readonly page = 'PersonaPicker'
  private readonly label = 'InviteGuardian'

  constructor(
    private seAnalytics: SeAnalyticsService,
    public addGuardianModalSubmitter: AddGuardianModalSubmitter,
    public seFeTranslationsLoader: SeFeTranslationsLoader,
    public toastService: SeFeToastService,
    public translateService: TranslateService
  ) {
    // noop
  }

  public ngOnInit(): void {
    this.loaded = false
    this.loadTranslations()
  }

  public loadTranslations(): void {
    this.seFeTranslationsLoader.load('se-persona-picker/feature-add-guardian-modal/assets/translations').then(() => {
      this.loaded = true
    })
  }

  public onCancel(invite: GuardianInvite): void {
    this.invites = this.invites.filter(i => i.email !== invite.email)
    if (!this.invites.length) {
      this.step = AddGuardianModalStep.Invite
    }
  }

  public onInviteAdded(invite: GuardianInvite): void {
    this.seClickEvent('SendInvite')
    if (this.guardianEmails.includes(invite.email) || this.invites.find(i => i.email === invite.email)) {
      this.error = this.translateService.instant(this.translationKey + 'NOTIFICATION.guardian_exists')
    }
    else {
      this.error = null
      this.invites.push(invite)
      this.step = AddGuardianModalStep.InviteReview
    }
  }

  public onSkip(): void {
    this.seClickEvent('Skip')
    this.error = null
    if (this.invites.length) {
      this.step = AddGuardianModalStep.InviteReview
    }
    else {
      this.close()
    }
  }

  public onStart(): void {
    this.step = AddGuardianModalStep.Invite
  }

  public open(persona: Persona, guardianEmails: string[]): void {
    this.error = null
    this.persona = persona
    this.onStart()
    this.invites = []
    this.guardianEmails = guardianEmails
    this.modal.open(undefined)
  }

  public onContinue(): void {
    this.error = null
    zip(...this.invites.map(invite => this.submitOne(invite))).subscribe((guardianships: cs.Households.PersonaGuardianInvite[]) => {
      const successGuardianships = guardianships.filter(guardianship => !!guardianship)
      if (successGuardianships.length === guardianships.length) {
        guardianships.forEach(guardianship =>
          this.toastService.success(this.translateService.instant(this.translationKey + 'NOTIFICATION.success')))
        this.close()
      }
      else {
        this.error = this.addGuardianModalSubmitter.error
        this.toastService.error(this.error)
      }
    })
  }

  public submitOne(invite: GuardianInvite): Observable<cs.Households.PersonaGuardianInvite> {
    return this.addGuardianModalSubmitter.submit(
      invite.access,
      invite.email,
      this.persona.id
    )
  }

  public onClose(): void {
    this.seClickEvent('Close')
    this.close()
  }

  public close(): void {
    this.modal.close()
    this.modalClosed.emit(this.persona)
  }

  public onAfterClose(): void {
    this.step = null
  }

  public seClickEvent(action: string): void {
    const clickAction = `${this.page}.${this.label}.${action}`
    try {
      this.seAnalytics.trackEvent('seEvent' ,
        {
          action: clickAction,
          type: 1
        }
      )
    } catch (e) {
      console.error('mParticle seEvent Error', e)
    }
  }
}
